import React, { useEffect, useMemo, useState } from "react";
import { Tab, Tabs } from "@mui/material";
import { formatBenchmarkChartValue } from "../../benchmarkMetricConfig";
import { buildKeywordDetailItems } from "./keywordDetailsUtils";
import styles from "./ResearchTopicsRankingView.module.scss";

const SCORE_METRIC = { key: "score", label: "Score", decimals: 2, align: "center" };

const ENTITY_TYPES = [
  { key: "institutions", label: "Top institutions", listKey: "top_institution", metrics: [SCORE_METRIC] },
  { key: "researchers", label: "Top researchers", listKey: "top_researcher", metrics: [SCORE_METRIC] },
  { key: "venues", label: "Top venues", listKey: "top_venue", metrics: [SCORE_METRIC] },
  { key: "units", label: "Top units", listKey: "top_unit", metrics: [SCORE_METRIC] },
];

const SKIP_INITIAL_WORDS = new Set(["of", "the", "at", "and", "–"]);

const getAbbreviation = (name) =>
  String(name || "")
    .split(/[\s,]+/)
    .filter(Boolean)
    .filter((w) => !SKIP_INITIAL_WORDS.has(w.toLowerCase()))
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 3) || "—";

const formatValue = (value, decimals = 0) =>
  formatBenchmarkChartValue(value, decimals);

const RankAvatar = ({ photoUrl, fallbackText, isInstitution }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [photoUrl]);

  const avatarClass = `${styles.avatar} ${
    isInstitution ? styles["avatar--institution"] : styles["avatar--other"]
  }`;

  if (photoUrl && !failed) {
    return (
      <img
        src={photoUrl}
        alt=""
        className={avatarClass}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <div
      className={`${avatarClass} ${styles["avatar--fallback"]}`}
      aria-hidden="true"
    >
      {fallbackText}
    </div>
  );
};

const RankingTable = ({ list, columns, isInstitution }) => {
  if (!Array.isArray(list) || list.length === 0) {
    return (
      <div className={styles.empty}>
        No ranking data available for this research topic.
      </div>
    );
  }

  const sortedRows = [...list].sort((a, b) => {
    const rankA = Number(a?.rank);
    const rankB = Number(b?.rank);
    const hasRankA = Number.isFinite(rankA) && rankA > 0;
    const hasRankB = Number.isFinite(rankB) && rankB > 0;
    if (hasRankA && hasRankB) return rankA - rankB;
    if (hasRankA) return -1;
    if (hasRankB) return 1;
    return 0;
  });

  return (
    <div className={styles["table-wrap"]}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles["table-head--rank"]}>Rank</th>
            <th className={styles["table-head--name"]}>Name</th>
            {columns.map((col) => (
              <th key={col.key} style={col.align ? { textAlign: col.align } : undefined}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((item, idx) => (
            <tr key={item?.id || `row-${idx}`}>
              <td className={styles["table-cell--rank"]}>
                {item?.rank != null ? `#${item.rank}` : "—"}
              </td>
              <td className={styles["table-cell--name"]}>
                <div className={styles["name-cell"]}>
                  <RankAvatar
                    photoUrl={item?.photo_url}
                    fallbackText={getAbbreviation(item?.name)}
                    isInstitution={isInstitution}
                  />
                  <span title={item?.name || ""}>{item?.name || "—"}</span>
                </div>
              </td>
              {columns.map((col) => (
                <td key={`${item?.id || idx}-${col.key}`} style={col.align ? { textAlign: col.align } : undefined}>
                  {item?.[col.key] == null
                    ? "—"
                    : formatValue(item[col.key], col.decimals || 0)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EntityRankingPanel = ({ raw, entityType }) => {
  const list = useMemo(() => {
    if (!raw) return [];
    const candidate = raw[entityType.listKey];
    return Array.isArray(candidate) ? candidate : [];
  }, [raw, entityType.listKey]);

  const visibleColumns = useMemo(
    () =>
      entityType.metrics.filter((metric) =>
        list.some((item) => item?.[metric.key] != null)
      ),
    [entityType.metrics, list]
  );

  return (
    <div className={styles.panel}>
      <div className={styles["scroll-area"]}>
        <RankingTable
          list={list}
          columns={visibleColumns}
          isInstitution={entityType.key === "institutions" || entityType.key === "units"}
        />
      </div>
    </div>
  );
};

const KeywordRankingPanel = ({ keywordItem }) => {
  const [entityKey, setEntityKey] = useState(ENTITY_TYPES[0].key);

  const entityType = useMemo(
    () => ENTITY_TYPES.find((t) => t.key === entityKey) || ENTITY_TYPES[0],
    [entityKey]
  );

  if (!keywordItem?.raw) {
    return (
      <div className={styles.empty}>
        No ranking data available for{" "}
        <strong>{keywordItem?.label || "this research topic"}</strong> yet.
      </div>
    );
  }

  return (
    <div className={styles["keyword-panel"]}>
      <Tabs
        value={entityKey}
        onChange={(_, value) => setEntityKey(value)}
        aria-label="Ranking entity type"
        variant="scrollable"
        scrollButtons="auto"
        classes={{
          root: styles["entity-tabs"],
          indicator: styles["tabs-indicator"],
        }}
      >
        {ENTITY_TYPES.map((type) => (
          <Tab
            key={type.key}
            value={type.key}
            label={type.label}
            sx={{ textTransform: "none" }}
            classes={{
              root: styles["entity-tab"],
              selected: styles["tab--selected"],
            }}
          />
        ))}
      </Tabs>

      {/* Re-mount per (keyword, entity) so per-entity metric state resets sensibly. */}
      <EntityRankingPanel
        key={`${keywordItem.key}-${entityType.key}`}
        raw={keywordItem.raw}
        entityType={entityType}
      />
    </div>
  );
};

const ResearchTopicsRankingView = ({
  data,
  selectedKeywords = [],
  keywordLabelMap = {},
}) => {
  const keywordItems = useMemo(
    () =>
      buildKeywordDetailItems({
        keywordDetails: data?.keyword_details,
        selectedKeywords,
        keywordLabelMap,
      }),
    [data, selectedKeywords, keywordLabelMap]
  );

  const [activeKeyword, setActiveKeyword] = useState(
    keywordItems[0]?.key || ""
  );

  // Snap to a still-valid keyword tab when the keyword set changes.
  useEffect(() => {
    if (!keywordItems.length) return;
    if (!keywordItems.some((item) => item.key === activeKeyword)) {
      setActiveKeyword(keywordItems[0].key);
    }
  }, [keywordItems, activeKeyword]);

  if (!data) return null;

  if (!keywordItems.length) {
    return (
      <section className={styles.section} aria-label="Research topics ranking">
        <div className={styles.empty}>
          No research topic ranking data available yet. Add research topics and
          run the analysis to populate this view.
        </div>
      </section>
    );
  }

  const activeItem =
    keywordItems.find((item) => item.key === activeKeyword) || keywordItems[0];

  return (
    <section className={styles.section} aria-label="Research topics ranking">
      <div className={styles.body}>
        <Tabs
          value={activeItem?.key || false}
          onChange={(_, value) => setActiveKeyword(value)}
          orientation="vertical"
          variant="scrollable"
          aria-label="Ranking research topic"
          classes={{
            root: styles["keyword-tabs"],
            indicator: styles["keyword-tabs-indicator"],
          }}
        >
          {keywordItems.map((item) => (
            <Tab
              key={item.key}
              value={item.key}
              label={item.label}
              sx={{ textTransform: "none" }}
              classes={{
                root: styles["keyword-tab"],
                selected: styles["keyword-tab--selected"],
              }}
            />
          ))}
        </Tabs>

        <div className={styles["keyword-content"]}>
          {/* Re-mount per active keyword so the entity tab resets to the first option. */}
          <KeywordRankingPanel key={activeItem.key} keywordItem={activeItem} />
        </div>
      </div>
    </section>
  );
};

export default ResearchTopicsRankingView;
